import { RefreshCw, ScanLine } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { OrderForm } from "../components/OrderForm";
import { api } from "../lib/api";
import type { OcrReview, OrderPayload } from "../types";

export function ReviewOcr() {
  const [reviews, setReviews] = useState<OcrReview[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selected = useMemo(
    () => reviews.find((review) => review.id === selectedId) ?? reviews[0],
    [reviews, selectedId],
  );

  async function load() {
    setBusy(true);
    setError("");

    try {
      const data = await api.reviews();
      setReviews(data.reviews);
      if (!selectedId && data.reviews[0]) {
        setSelectedId(data.reviews[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function recognize() {
    if (!selected) {
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const data = await api.recognize(selected.id);
      setReviews((current) =>
        current.map((item) => (item.id === selected.id ? data.review : item)),
      );
      setMessage("识别完成");
    } catch (err) {
      setError(err instanceof Error ? err.message : "识别失败");
    } finally {
      setBusy(false);
    }
  }

  async function confirm(order: OrderPayload) {
    if (!selected) {
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      await api.updateReview(selected.id, order);
      await api.confirmReview(selected.id, order);
      setMessage("订单已确认入库");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "确认失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Review OCR</p>
          <h1>识别确认</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={17} aria-hidden />
          刷新
        </button>
      </header>

      {message ? <div className="notice success">{message}</div> : null}
      {error ? <div className="notice danger">{error}</div> : null}

      <div className="review-grid">
        <aside className="panel review-list">
          {reviews.length === 0 ? (
            <p className="empty">暂无 OCR 记录</p>
          ) : (
            reviews.map((review) => (
              <button
                className={
                  review.id === selected?.id
                    ? "review-item active"
                    : "review-item"
                }
                key={review.id}
                onClick={() => setSelectedId(review.id)}
                type="button"
              >
                <strong>{review.status}</strong>
                <span>{review.provider}</span>
                <em>{review.screenshot_key}</em>
              </button>
            ))
          )}
        </aside>

        <section className="panel">
          {selected ? (
            <>
              <div className="panel-title split">
                <div>
                  <h2>确认字段</h2>
                  <p className="muted-text">{selected.screenshot_key}</p>
                </div>
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={recognize}
                  type="button"
                >
                  <ScanLine size={17} aria-hidden />
                  重新识别
                </button>
              </div>
              <OrderForm
                busy={busy}
                initialValue={selected.extracted_json}
                onSubmit={confirm}
                submitLabel="确认入库"
              />
              {selected.raw_text ? (
                <details className="raw-box">
                  <summary>查看原始 OCR 文本</summary>
                  <pre>{selected.raw_text}</pre>
                </details>
              ) : null}
            </>
          ) : (
            <p className="empty">请选择 OCR 记录</p>
          )}
        </section>
      </div>
    </section>
  );
}
